import styled, { css, keyframes } from 'styled-components';

import {
  AFFILIATE_FORM,
  AFFILIATE_FORM_ANIM,
  ANIM,
  CASINO_SETTINGS,
  CASINO_SHEET,
  COLORS,
  DESKTOP,
  GRADIENTS,
  MOBILE,
  NAV_DESKTOP,
  SPACE,
} from '../../theme/tokens';
import { contentAreaOverlay } from '../../theme/contentAreaOverlay';
import { Text } from '../Text/Text';

// Reuses the affiliate-form overlay kit (tokens + choreography): a centred modal
// on desktop, a bottom sheet on mobile, both portalled over the whole viewport.
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/** The mirror of the exit: the dialog rises into place from below the viewport. Used at
 *  every breakpoint, so a modal arrives and leaves the same way it does as a sheet. */
const slideIn = keyframes`
  from { transform: translateY(120%); }
  to { transform: translateY(0); }
`;

// Exit choreography (mirrors the entry) so closing is animated, not an instant
// unmount — backdrop fades, desktop modal scales out, mobile sheet slides down.
const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;
const scaleOut = keyframes`
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.96); }
`;
export const Backdrop = styled.div<{ $closing?: boolean; $deepened?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;

  /* The scrim is a LAYER, not this element's own background: the dialog is a CHILD of this
   * element, so animating opacity here fades the dialog along with the dim — which is
   * exactly the fade the sheet is meant to be free of on the way out. Keeping the dim on a
   * pseudo-element lets it fade on its own while the sheet only ever slides. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: ${AFFILIATE_FORM.backdrop};
    /* The dim is paced by the SHEET, in both directions: same duration, same curve. Given
     * its own faster timing it lands (or vanishes) well before the sheet arrives or
     * leaves, and the two read as separate events rather than one movement. */
    animation: ${(p) => (p.$closing ? fadeOut : fadeIn)}
      ${(p) => (p.$closing ? CASINO_SHEET.CLOSE_MS : CASINO_SHEET.OPEN_MS)}ms
      ${(p) => (p.$closing ? CASINO_SHEET.EXIT_EASE : AFFILIATE_FORM_ANIM.EASE)} both;
  }

  /* Self-contained box model — don't depend on a host border-box reset. */
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  ${MOBILE} {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }

  /* The stage's height, for a dialog that fills it (the composers' splits size from it): the viewport, until the
     backdrop is pinned to the content card (from NAV_DESKTOP up), then the card. Published here so a filling
     dialog always matches where the backdrop really is, never a box it only sometimes is. */
  --sheet-stage-height: 100dvh;

  ${contentAreaOverlay}

  ${NAV_DESKTOP} {
    --sheet-stage-height: var(--content-height, 100dvh);
  }
`;

/**
 * A full-viewport wash over EVERYTHING behind the dialog, faded in when the caller asks
 * for it (the replace-logo overlay does while a file is dragged over the page).
 *
 * Deliberately not part of {@link Backdrop}: on desktop that is re-pinned to the content
 * card (see `contentAreaOverlay`), so the side menu and the Lumio column fall outside it
 * and would stay bright. This sits one layer below the backdrop's z-index — above all the
 * app chrome, below the dialog itself — and is always mounted so it can fade BACK out.
 */
/**
 * How far everything except the drop target recedes while a drag is live. Shared with the
 * replace overlay's own body sections, so the dialog fades as ONE surface: header, body
 * and footer all land on the same value.
 */
export const DRAG_DIM_OPACITY = 0.25;

export const ViewportScrim = styled.div<{ $on: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1999;
  pointer-events: none;
  background: ${CASINO_SETTINGS.dropScrim};
  opacity: ${(p) => (p.$on ? 1 : 0)};
  transition: opacity ${ANIM.LOGO_DROP_DIM_MS}ms ${ANIM.EASE};
`;

export const Surface = styled.div<{
  $closing?: boolean;
  $maxWidth?: string;
  $maxHeight?: string;
  $animateResize?: boolean;
  /** Pixels the owner has dragged the sheet down by (mobile dismiss gesture). */
  $dragY?: number;
  /** True while their finger is down: the sheet must track it with no easing. */
  $dragging?: boolean;
  /** True once this sheet has been dragged at all, for the rest of its life. */
  $dragged?: boolean;
  /** Another dialog has opened on top of this one. */
  $behind?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  /* It takes the focus as it opens (useDialogFocus): its controls show the keyboard's ring, the sheet none. */
  outline: none;
  /* The cap has to live on WIDTH, not on a \`max-width\` beside it: this is a width, so a
   * \`max-width: 100%\` alongside would resolve against 560px and change nothing. Callers
   * widen the dialog by raising the first term (\`maxWidth\`); the \`100%\` keeps it inside
   * the backdrop at every size. Mobile overrides this below. */
  width: min(${(p) => p.$maxWidth ?? '560px'}, 100%);
  /* Content-sized up to a cap: the sheet only grows as tall as its content needs,
   * never past the cap. Default keeps the standard 720px ceiling; callers can pass
   * a taller cap (e.g. \`100%\` = the full available viewport, minus the backdrop's
   * 24px top/bottom gap) via the \`maxHeight\` prop. Mobile keeps its own cap below. */
  max-height: ${(p) => p.$maxHeight ?? 'min(720px, 100%)'};
  overflow: hidden;
  border-radius: 16px;
  background: ${AFFILIATE_FORM.surface};
  /* Large soft dark drop shadow so the modal lifts off a LIGHT backdrop (the scrim
   *  only dims faintly). Tokenised dark shadow, shared across the studio's modals. */
  box-shadow: ${AFFILIATE_FORM.modalShadow};
  /* Entrance is a keyframe; the EXIT is a transform transition instead (see $closing
   * below), so a sheet the owner has already dragged partway down carries on from where
   * they left it rather than snapping back to 0 to replay a keyframe. */
  /* Fill mode is BACKWARDS, never both. With both, the finished entrance keeps imposing its
   * own end transform, so when the exit removes the animation and sets the closing
   * transform in the SAME style change there is nothing to transition FROM and the sheet
   * teleports off screen — which reads as it vanishing (or fading, with the scrim going at
   * the same time) rather than leaving. Backwards still covers the pre-start frame, and the
   * animation's end state matches the base transform, so nothing jumps at the handover. */
  /* Keyed on $dragged, not $dragY: naming the keyframe again after a drag ends starts a NEW
   * animation (animation-name went to none while the finger was down), so a sheet let go
   * under the dismiss threshold would replay its whole entrance instead of settling back
   * over SETTLE_MS. Once dragged, this sheet's transform belongs to the transition below. */
  animation: ${(p) =>
    p.$closing || p.$dragY || p.$dragged
      ? 'none'
      : css`${slideIn} ${CASINO_SHEET.OPEN_MS}ms ${AFFILIATE_FORM_ANIM.EASE} backwards`};
  /* The exit and the dismiss gesture are the same thing: a transform. Dragging tracks the
   * finger with no easing; letting go under the threshold settles back to 0; closing runs
   * it to 100% over the close duration. Opacity is deliberately untouched — the sheet
   * slides out of the viewport rather than fading away under the owner's finger. */
  /* Out of the VIEWPORT, not by a multiple of the dialog's OWN height: a short confirm
   * dialog is a couple of hundred pixels tall, so a self-relative travel leaves it sitting
   * mid-screen when it unmounts — which, with the scrim going at the same time, reads as
   * it fading away rather than leaving. A viewport-height travel clears any dialog. */
  transform: ${(p) => (p.$closing ? 'translateY(100dvh)' : `translateY(${p.$dragY ?? 0}px)`)}
    scale(var(--ows-overlay-stack-scale, 1));
  will-change: transform;

  /* When the caller opts in (\`animateResize\`), the sheet's explicit height is
   * driven by a ResizeObserver in Sheet.tsx and animated here, so a content
   * swap (tab change, a section appearing) grows/shrinks the modal smoothly
   * instead of snapping. Same timing/easing as the open choreography. */
  transition: ${(p) => {
    const move = p.$dragging
      ? 'none'
      : p.$closing
        ? `transform ${CASINO_SHEET.CLOSE_MS}ms ${CASINO_SHEET.EXIT_EASE}`
        : `transform ${CASINO_SHEET.SETTLE_MS}ms ${AFFILIATE_FORM_ANIM.EASE}`;
    const height = p.$animateResize ? `, height ${AFFILIATE_FORM_ANIM.SHEET_MS}ms ${AFFILIATE_FORM_ANIM.EASE}` : '';
    return `${move}${height}`;
  }};

  /* A dialog with another one on top of it settles back, so the stack reads as depth
   * rather than as two dialogs competing for the same plane. Desktop only: stacked bottom
   * sheets are pinned to the bottom edge, and shrinking one there just detaches it. */
  ${DESKTOP} {
    --ows-overlay-stack-scale: ${(p) => (p.$behind ? 0.9 : 1)};
  }

  ${MOBILE} {
    width: 100%;
    max-height: calc(100% - 56px);
    margin-top: auto;
    border-radius: 24px 24px 0 0;
    /* Same large soft dark shadow as desktop, cast UPWARD from the bottom sheet. */
    box-shadow: ${AFFILIATE_FORM.modalShadowUp};
  }
`;

// `$dim` recedes the dialog's own chrome alongside its body (see `deepenBackdrop`), so a
// drag leaves the drop target as the only lit thing in the dialog.
/** The strip an owner drags the sheet down by: the whole width above the title, the bar drawn at its top (it takes the
 *  header's top padding, so a finger anywhere over the title's top margin catches it). Phone only, there is no sheet
 *  otherwise. */
export const DragHandle = styled.div`
  display: none;

  ${MOBILE} {
    display: flex;
    justify-content: center;
    /* The header's 20px top padding lives here now: 36px over the title, as before, so the layout is unchanged. */
    padding: ${SPACE[12]} ${SPACE[0]} ${SPACE[20]};
    touch-action: none;
    cursor: grab;

    &::after {
      content: '';
      width: 40px;
      height: 4px;
      border-radius: 99px;
      background: ${COLORS.hairline};
    }
  }
`;

export const Header = styled.div<{ $dim?: boolean }>`
  flex: 0 0 auto;
  opacity: ${(p) => (p.$dim ? DRAG_DIM_OPACITY : 1)};
  transition: opacity ${ANIM.LOGO_DROP_DIM_MS}ms ${ANIM.EASE};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 24px 24px 18px;
  border-bottom: 1px solid ${AFFILIATE_FORM.hairline};

  ${MOBILE} {
    /* No top padding on a phone: the drag strip above holds it (see DragHandle). 18px: the sheet's phone inset,
       shared with its body and footer. */
    padding: ${SPACE[0]} 18px ${SPACE[16]};
  }
`;

export const Title = styled(Text)`
  font-size: 17px;
  font-weight: 900;
  line-height: 1.15;
  background: ${GRADIENTS.backHeaderTitle};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

export const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border: none;
  border-radius: 99px;
  background: ${AFFILIATE_FORM.closeCircle};
  cursor: pointer;

  /* While the dialog is busy (a card confirm, an upload) the close is refused, so it has to
   * LOOK refused. A live-looking button that does nothing reads as a broken page at exactly
   * the moment the owner most wants out. */
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

export const ScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Reserve the scrollbar gutter so a content-height change inside the sheet (switching
   * Ask Lumio / Restyle / Upload, a section appearing) can't slide the body sideways as the
   * bar toggles. No-op on mobile, where scrollbars are hidden (index.scss). */
  scrollbar-gutter: stable;
`;

/** On a phone the sheet runs to the screen's bottom edge, so whatever ends it keeps clear of the OS home indicator: the
 *  inset the OS reports (about 34px on an iPhone with the bar), and at least 12px where it reports none (Android's
 *  gesture bar, an emulator). */
const HOME_INDICATOR_CLEARANCE = `max(env(safe-area-inset-bottom, 0px), ${SPACE[12]})`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px 24px;

  ${MOBILE} {
    padding: 16px 18px 22px;

    /* No footer below: the body ends the sheet, so it clears the home indicator itself. */
    ${ScrollArea}:last-child > & {
      padding-bottom: calc(22px + ${HOME_INDICATOR_CLEARANCE});
    }
  }
`;

export const Footer = styled.div<{ $dim?: boolean }>`
  flex: 0 0 auto;
  opacity: ${(p) => (p.$dim ? DRAG_DIM_OPACITY : 1)};
  transition: opacity ${ANIM.LOGO_DROP_DIM_MS}ms ${ANIM.EASE};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid ${AFFILIATE_FORM.hairline};
  background: ${AFFILIATE_FORM.surface};

  ${MOBILE} {
    padding: 14px 18px calc(14px + ${HOME_INDICATOR_CLEARANCE});
  }
`;
